import React, { useRef, useState } from "react";
import { Download, Share2, X, Loader2, Check } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { VipMembershipCard } from "./VipMembershipCard";

const FORMATS = [
    { key: "square", label: "Square · IG / FB / TikTok", width: 1080, height: 1080 },
    { key: "story", label: "Story · IG / TikTok 9:16", width: 1080, height: 1920 },
    { key: "wide", label: "Wide · X / Twitter 16:9", width: 1600, height: 900 },
    { key: "card", label: "Card · 1.586:1", width: 1600, height: 1009 },
];

/**
 * Generates a premium shareable VIP card image using html2canvas. Supports
 * multiple aspect ratios optimised for FB/IG/IG-Story/TikTok/X. Also exposes
 * a "Share My VIP Status" button that uses the native Web Share API where
 * supported and falls back to clipboard.
 */
export const VipCardShareModal = ({ open, onClose, account }) => {
    const renderRef = useRef(null);
    const [format, setFormat] = useState("square");
    const [busy, setBusy] = useState(false);

    const customer = account?.user || {};
    const tier = customer.vip_tier || "none";
    const lifetime = Number(customer.lifetime_spend || 0);
    const discountPct = Number(customer.vip_discount_pct || 0);
    const customerName = customer.name || customer.email || "Carbon Researcher";

    const config = FORMATS.find((f) => f.key === format) || FORMATS[0];

    const render = async () => {
        if (!renderRef.current) return null;
        const canvas = await html2canvas(renderRef.current, {
            backgroundColor: "#0a0905",
            scale: 2,
            useCORS: true,
            logging: false,
        });
        return canvas;
    };

    const handleDownload = async () => {
        setBusy(true);
        try {
            const canvas = await render();
            if (!canvas) return;
            const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 1.0));
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `carbon-labs-vip-${tier}-${format}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("VIP card downloaded");
        } catch (e) {
            toast.error("Could not generate card image");
        }
        setBusy(false);
    };

    const handleShare = async () => {
        setBusy(true);
        try {
            const canvas = await render();
            const blob = canvas && (await new Promise((res) => canvas.toBlob(res, "image/png", 1.0)));
            const shareText = `I just unlocked ${tier.toUpperCase()} VIP status at Carbon Labs — ${discountPct}% off every order. Join me at https://carbonlabsau.com`;
            if (blob && navigator.canShare && navigator.canShare({ files: [new File([blob], "vip.png", { type: "image/png" })] })) {
                await navigator.share({
                    title: "My Carbon Labs VIP Status",
                    text: shareText,
                    files: [new File([blob], `carbon-labs-vip-${tier}.png`, { type: "image/png" })],
                });
            } else if (navigator.share) {
                await navigator.share({ title: "Carbon Labs VIP", text: shareText, url: "https://carbonlabsau.com" });
            } else {
                await navigator.clipboard.writeText(shareText);
                toast.success("Share text copied to clipboard");
            }
        } catch (e) {
            if (e?.name !== "AbortError") toast.error("Could not share");
        }
        setBusy(false);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                className="max-w-2xl p-0 overflow-hidden border-0 bg-[var(--bg-elevated)]"
                data-testid="vip-share-modal"
            >
                <DialogTitle className="sr-only">My Carbon VIP Card</DialogTitle>
                <DialogDescription className="sr-only">
                    Download or share your Carbon Labs VIP membership card in formats optimised for social media.
                </DialogDescription>
                <div className="p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-3 mb-4">
                        <div>
                            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)]">
                                Share your status
                            </p>
                            <h3 className="font-display text-xl sm:text-2xl font-black text-white mt-0.5">
                                My Carbon VIP Card
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-black/40 border border-white/15 flex items-center justify-center hover:bg-black/60"
                            data-testid="vip-share-close"
                            aria-label="Close"
                        >
                            <X size={14} className="text-white/80" />
                        </button>
                    </div>

                    {/* Live preview */}
                    <div
                        ref={renderRef}
                        className="mx-auto"
                        style={{
                            width: "100%",
                            maxWidth: "480px",
                            aspectRatio: `${config.width} / ${config.height}`,
                            background: "linear-gradient(135deg, #0a0905 0%, #14181d 100%)",
                            borderRadius: format === "story" || format === "square" ? "24px" : "16px",
                            padding: format === "story" ? "32px" : "20px",
                            position: "relative",
                            overflow: "hidden",
                            border: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        <div className="absolute inset-0 pointer-events-none"
                            style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(233,188,11,0.15), transparent 50%), radial-gradient(circle at 80% 80%, rgba(233,188,11,0.10), transparent 50%)" }} />
                        <div className="relative z-10 h-full flex flex-col justify-between">
                            <div className="text-center mb-2">
                                <p className="font-display text-lg sm:text-xl font-black text-white tracking-[0.2em]">
                                    CARBON LABS
                                </p>
                                <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.4em] text-[var(--gold)]">
                                    Premium VIP Member
                                </p>
                            </div>
                            <div className="flex-1 flex items-center justify-center">
                                <div style={{ width: "100%", maxWidth: format === "story" ? "100%" : "380px" }}>
                                    <VipMembershipCard
                                        tier={tier}
                                        customerName={customerName}
                                        lifetimeSpend={lifetime}
                                        discountPct={discountPct}
                                    />
                                </div>
                            </div>
                            <p className="text-center text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mt-3">
                                carbonlabsau.com
                            </p>
                        </div>
                    </div>

                    {/* Format pills */}
                    <div className="flex flex-wrap gap-2 mt-4">
                        {FORMATS.map((f) => (
                            <button
                                key={f.key}
                                onClick={() => setFormat(f.key)}
                                className={`px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-all ${
                                    format === f.key
                                        ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                                }`}
                                data-testid={`vip-share-format-${f.key}`}
                            >
                                {format === f.key && <Check size={10} className="inline mr-1" />}
                                {f.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 mt-5">
                        <button
                            onClick={handleDownload}
                            disabled={busy}
                            className="btn-solid flex-1"
                            data-testid="vip-share-download"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            Download VIP Card
                        </button>
                        <button
                            onClick={handleShare}
                            disabled={busy}
                            className="btn-neon flex-1"
                            data-testid="vip-share-native"
                        >
                            <Share2 size={14} /> Share My VIP Status
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
